import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { map, Observable, take } from 'rxjs';

import { extractLabels } from './extractLabels';

/**
 * An (optional) build identifier string which will be appended to translation file URLs as query parameter to do cache busting.
 *
 * Will ensure that, after a server-side upgrade of a web application, clients will fetch the new translation files instead of
 * using cached versions.
 */
export const TRANSLATION_HTTP_REQUEST_BUILD_IDENTIFIER = new InjectionToken<string>(
	'TRANSLATION_HTTP_REQUEST_BUILD_IDENTIFIER'
);

@Injectable()
export class TranslocoHttpLoader implements TranslocoLoader {
	private readonly buildIdentifier: string;

	/**
	 * @param http HttpClient for loading translation files
	 * @param buildIdentifier Optional build identifier string. Appended to translation file URLs as query parameter for cache busting
	 */
	constructor(
		private readonly http: HttpClient,
		@Optional() @Inject(TRANSLATION_HTTP_REQUEST_BUILD_IDENTIFIER) buildIdentifier?: string
	) {
		this.buildIdentifier = buildIdentifier ? encodeURIComponent(buildIdentifier) : '';
	}

	public getTranslation(langPath: string): Observable<Translation> {
		const cacheBustAppend = this.buildIdentifier ? `?i18n-cb=${this.buildIdentifier}` : '';
		const langUrl = `./assets/i18n/${langPath}.json${cacheBustAppend}`;
		const labels = this.http.get<Translation>(langUrl).pipe(
			take(1),
			map((labels) => extractLabels(labels))
		);

		return labels;
	}
}
